import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config';

const CreateThreadModal = ({ isOpen, onClose, showToast, onThreadCreated }) => {
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('discussion');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [codeLanguage, setCodeLanguage] = useState('');
  const [codeContent, setCodeContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (title.length < 10) {
      showToast('Title must be at least 10 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const threadData = {
        title,
        content,
        type,
        category,
        tags: tags.split(',').map(t => t.trim()).filter(t => t),
        codeBlocks: codeContent ? [{ language: codeLanguage || 'text', code: codeContent }] : []
      };

      const response = await fetch(getApiUrl('/api/threads'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(threadData)
      });

      if (response.ok) {
        const data = await response.json();
        showToast('Thread created successfully!');
        onThreadCreated && onThreadCreated(data.thread);
        handleClose();
      } else {
        const error = await response.json();
        // Show moderation rejection reason if available
        if (error.reason) {
          showToast(error.reason);
        } else {
          showToast(error.error || 'Failed to create thread');
        }
      }
    } catch (error) {
      showToast('Failed to create thread');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setContent('');
    setType('discussion');
    setCategory('general');
    setTags('');
    setCodeLanguage('');
    setCodeContent('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay active" onClick={(e) => {
      if (e.target.className.includes('modal-overlay')) handleClose();
    }}>
      <div className="modal" style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Create New Thread</h2>
          <button 
            onClick={handleClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              placeholder="What's your question or topic?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              minLength={10}
              maxLength={200}
            />
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px', display: 'block' }}>
              {title.length}/200 characters (min 10)
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '14px',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="question">Question</option>
                <option value="discussion">Discussion</option>
                <option value="project">Project</option>
                <option value="experiment">Experiment</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '14px',
                  color: 'var(--text-primary)'
                }}
              >
                <optgroup label="AI & Data">
                  <option value="machine-learning">Machine Learning</option>
                  <option value="data-science">Data Science</option>
                  <option value="computer-vision">Computer Vision</option>
                  <option value="nlp">NLP</option>
                </optgroup>
                <optgroup label="Math & Theory">
                  <option value="algorithms">Algorithms</option>
                  <option value="statistics">Statistics</option>
                  <option value="mathematics">Mathematics</option>
                  <option value="cryptography">Cryptography</option>
                </optgroup>
                <optgroup label="Web & Mobile">
                  <option value="web-dev">Web Dev</option>
                  <option value="frontend">Frontend</option>
                  <option value="backend">Backend</option>
                  <option value="mobile">Mobile</option>
                  <option value="api-design">API Design</option>
                </optgroup>
                <optgroup label="Infrastructure">
                  <option value="systems">Systems</option>
                  <option value="devops">DevOps</option>
                  <option value="cloud">Cloud</option>
                  <option value="databases">Databases</option>
                  <option value="distributed-systems">Distributed Systems</option>
                  <option value="networking">Networking</option>
                  <option value="architecture">Architecture</option>
                  <option value="performance">Performance</option>
                </optgroup>
                <optgroup label="Core CS">
                  <option value="programming-languages">Languages</option>
                  <option value="compilers">Compilers</option>
                  <option value="operating-systems">Operating Systems</option>
                  <option value="testing">Testing</option>
                </optgroup>
                <optgroup label="Specialized">
                  <option value="security">Security</option>
                  <option value="game-dev">Game Dev</option>
                  <option value="embedded">Embedded</option>
                  <option value="blockchain">Blockchain</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="career">Career</option>
                  <option value="general">General</option>
                </optgroup>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Content</label>
            <textarea
              placeholder="Describe your question, project, or topic in detail..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '14px',
                color: 'var(--text-primary)',
                minHeight: '120px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          <div className="form-group">
            <label>Tags (comma separated)</label>
            <input
              type="text"
              placeholder="e.g., python, machine-learning, pytorch"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
              Code Block (optional)
            </label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Language (e.g., python, javascript)"
                value={codeLanguage}
                onChange={(e) => setCodeLanguage(e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
            <textarea
              placeholder="Paste your code here..."
              value={codeContent}
              onChange={(e) => setCodeContent(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'var(--bg-code)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '13px',
                color: '#e4e4e7',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: "'JetBrains Mono', monospace"
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button type="button" className="btn btn-ghost" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Thread'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateThreadModal;

